import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import { money, invoiceTotal } from '../../shared/formatters';
import Breadcrumbs from '../../shared/components/Breadcrumbs';
import { portalApi } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

let lineSeq = 0;
const nextLineId = () => `new_line_${Date.now()}_${lineSeq++}`;

const emptyLine = () => ({ id: nextLineId(), description: '', quantity: 1, unit_price: 0 });

// Quote form (pass 5 §5E, pass 7 §7B) — full page: client picker, dynamic
// line items with live totals, valid_until, notes. "Draft with AI" asks
// /api/quotes/draft for a tenant-grounded editable draft (catalog + past
// quotes — never auto-sent, always editable).
export default function QuoteForm() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, add, update } = useData();
  const { notify } = useToast();

  const editing = Boolean(quoteId);
  const existing = editing ? data.quotes.find((item) => item.id === quoteId) : null;

  const [form, setForm] = useState(() => existing ? {
    client_id: existing.client_id,
    line_items: existing.line_items.map((item) => ({ ...item })),
    status: existing.status,
    valid_until: String(existing.valid_until || '').slice(0, 10),
    notes: existing.notes || '',
  } : {
    client_id: searchParams.get('client') || '',
    line_items: [emptyLine()],
    status: 'draft',
    valid_until: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [jobDescription, setJobDescription] = useState('');

  const client = data.clients.find((item) => item.id === form.client_id);
  const total = useMemo(() => invoiceTotal(form), [form]);

  const setLine = (id, patch) => setForm((current) => ({
    ...current,
    line_items: current.line_items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  }));

  const addLine = () => setForm((current) => ({ ...current, line_items: [...current.line_items, emptyLine()] }));

  const removeLine = (id) => setForm((current) => ({
    ...current,
    line_items: current.line_items.length > 1 ? current.line_items.filter((item) => item.id !== id) : current.line_items,
  }));

  // AI-assisted draft — grounded in the tenant's pricing catalog + quote
  // history by the server; the result REPLACES the editable line items.
  const draftWithAi = async () => {
    if (!form.client_id) {
      notify('Pick a client first', 'error');
      return;
    }
    if (!jobDescription.trim()) {
      notify('Describe the work first', 'error');
      return;
    }
    setDrafting(true);
    try {
      const result = await portalApi.draftQuote({
        tenant_id: data.tenant.id,
        client_id: form.client_id,
        job_description: jobDescription,
      });
      const items = (result.line_items || []).map((item) => ({ id: nextLineId(), ...item, total: (Number(item.qty) || 1) * (Number(item.unit_price) || 0) }));
      setForm((current) => ({
        ...current,
        line_items: items.map((item) => ({
          id: item.id,
          description: item.description,
          quantity: Number(item.qty) || 1,
          unit_price: Number(item.unit_price) || 0,
        })),
      }));
      notify(`Draft loaded from ${result.source === 'llm' ? 'AI provider' : 'your price catalogue'} — review every line before sending`);
    } catch (err) {
      notify(err.message || 'Drafting failed', 'error');
    } finally {
      setDrafting(false);
    }
  };

  const save = async (statusOverride) => {
    if (!form.client_id) {
      notify('Pick a client first', 'error');
      return;
    }
    if (!form.line_items.some((item) => item.description.trim())) {
      notify('Add at least one line item', 'error');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, status: statusOverride || form.status };
      const saved = editing
        ? await update('quotes', quoteId, { ...existing, ...payload })
        : await add('quotes', payload);

      // "Send" also simulates the client notification (audit trail) —
      // the real send channel is the portal; the log row records intent.
      if ((statusOverride || form.status) === 'sent') {
        try {
          await add('notification_log', {
            channel: 'email',
            recipient: client?.email || '',
            context: `quote_sent:${saved.id}`,
            status: 'sent',
            created_at: new Date().toISOString(),
          });
        } catch {
          // audit is best-effort
        }
      }

      notify(statusOverride === 'sent' ? 'Quote sent to the client portal' : 'Quote saved');
      navigate(`/admin/quotes/${saved.id}`);
    } catch (err) {
      notify(err.message || 'Failed to save quote', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="standalone-page">
      <Breadcrumbs items={[{ label: 'Quotes', to: '/admin/quotes' }, { label: editing ? (existing?.number || 'Edit quote') : 'New quote' }]} />

      <header className="simple-header">
        <div>
          <span className="eyebrow">Estimates &amp; approvals</span>
          <h1>{editing ? `Edit ${existing?.number || 'quote'}` : 'New quote'}</h1>
        </div>
        <div className="header-actions">
          <button className="secondary-button" onClick={() => navigate('/admin/quotes')} disabled={busy}>Cancel</button>
          <button className="secondary-button" onClick={() => save()} disabled={busy}>{busy ? 'Saving…' : 'Save draft'}</button>
          <button className="primary-button" onClick={() => save('sent')} disabled={busy}>{busy ? 'Saving…' : 'Save & send'}</button>
        </div>
      </header>

      <div className="quote-form-grid">
        <section className="panel quote-main">
          <div className="panel-head"><h3>Details</h3></div>
          <label>Client
            <select value={form.client_id} onChange={(event) => setForm((current) => ({ ...current, client_id: event.target.value }))} disabled={editing}>
              <option value="">Choose a client…</option>
              {data.clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <div className="form-row">
            <label>Valid until<input type="date" value={form.valid_until} onChange={(event) => setForm((current) => ({ ...current, valid_until: event.target.value }))} /></label>
            <label>Status
              <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                <option value="draft">Draft</option>
                <option value="sent">Awaiting approval</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
                <option value="expired">Expired</option>
              </select>
            </label>
          </div>
          <label>Notes (shown on the quote)<textarea rows="2" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></label>
        </section>

        <section className="panel quote-draft">
          <div className="panel-head"><h3>Draft with AI</h3></div>
          <p className="field-hint">Describe the work — we&apos;ll pre-fill line items from your price catalogue and past quotes. Everything stays editable.</p>
          <label>Work description
            <textarea rows="3" value={jobDescription} onChange={(event) => setJobDescription(event.target.value)} placeholder="e.g. Annual boiler service with CP12, replace pressure relief valve" />
          </label>
          <button type="button" className="secondary-button" onClick={draftWithAi} disabled={drafting || !data.pricingCatalog.length}>
            <SafeIcon icon={Icons.Bell} /> {drafting ? 'Drafting…' : 'Draft with AI'}
          </button>
          {!data.pricingCatalog.length && <p className="field-hint">Add items to Settings → Price catalogue first — drafts are grounded in your own prices.</p>}
        </section>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h3>Line items</h3>
          <button type="button" className="secondary-button small-button" onClick={addLine}>+ Add line</button>
        </div>
        <table className="line-items-table">
          <thead>
            <tr><th>Description</th><th>Qty</th><th>Unit price</th><th>Line total</th><th /></tr>
          </thead>
          <tbody>
            {form.line_items.map((item) => (
              <tr key={item.id}>
                <td><input value={item.description} onChange={(event) => setLine(item.id, { description: event.target.value })} placeholder="Description" /></td>
                <td><input type="number" min="0" step="1" value={item.quantity} onChange={(event) => setLine(item.id, { quantity: Number(event.target.value) })} /></td>
                <td><input type="number" min="0" step="0.01" value={item.unit_price} onChange={(event) => setLine(item.id, { unit_price: Number(event.target.value) })} /></td>
                <td className="line-total">{money((Number(item.quantity) || 0) * (Number(item.unit_price) || 0))}</td>
                <td><button type="button" className="icon-button" aria-label="Remove line" onClick={() => removeLine(item.id)} disabled={form.line_items.length <= 1}><SafeIcon icon={Icons.X} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="invoice-total"><span>Total</span><strong>{money(total)}</strong></div>
      </section>
    </div>
  );
}
